import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useState } from 'react';
import { PageTitle } from '../../../../../components/PageTitle';
import { UploadPhotoWithMiniature } from '../../../../../components/UploadPhotoWithMiniature';
import { createMainBannerBreadcrumbs } from '../../../../../constants/BreadCrumbs/createMainBannerItem';
import { useTypeSelector } from '../../../../../store';
import { selectSiteBodyBlock } from '../../../../../selectors/mainPage';
import {
    BannerItemForEdit,
    WebBlockKey,
} from '../../../../../types/Models/MainPage/MainPageModel';
import { editBannerItemsWeb } from '../../../../../actions/mainPage';
import { SubmitButtons } from '../../../../../components/StyledComponents';
import Switch from '../../../../../components/sw';
import { BreadcrumbsMainBanners } from '../../../../../components/BreadcrumbsMainBanners';
import * as S from '../EditMainBannerItem/styles';
import { PageStatus } from '../constants';
import { CreateProps } from '../EditMainBannerItem/constants';
import { MainBannerItem } from './constants';
import { UploadVideoWithMiniature } from '../../../../../components/UploadVideoWithMiniature';

export const CreateMainBanner = ({ changePage }: CreateProps) => {
    const dispatch = useDispatch();

    const siteBodyBlock = useTypeSelector(
        selectSiteBodyBlock(WebBlockKey.WEB_MAIN),
    );

    const [fieldError, setFieldError] = useState({
        image: '',
        mobileImage: '',
        video: '',
        mobileVideo: '',
    });

    const resetErrors = () => {
        setFieldError({
            image: '',
            mobileImage: '',
            video: '',
            mobileVideo: '',
        });
    };

    const errorValues = Object.values(fieldError);

    const handleSubmit = (values: MainBannerItem) => {
        const nextFieldError = {
            image: '',
            mobileImage: '',
            video: '',
            mobileVideo: '',
        };
        console.log('values', values);
        if (values.fileImage?.id && !values.mobileFileImage?.id) {
            nextFieldError.mobileImage = 'Загрузите изображение для мобильной версии сайта';
        }

        if (!values.fileImage?.id && values.mobileFileImage?.id) {
            nextFieldError.image = 'Загрузите изображение для desktop версии сайта';
        }

        if (values.fileVideo?.id && !values.mobileFileVideo?.id) {
            nextFieldError.mobileVideo = 'Загрузите видео для мобильной версии сайта';
        }

        if (!values.fileVideo?.id && values.mobileFileVideo?.id) {
            nextFieldError.video = 'Загрузите видео для desktop версии сайта';
        }

        setFieldError(nextFieldError);

        const hasErrors = Object.values(nextFieldError).some(v => v !== '');

        if (hasErrors) return;

        if (errorValues.length > 0 && errorValues.every(v => v === '')) {
            const requestData: BannerItemForEdit = {
                id: values.id,
                title: values.title || '',
                buttonText: values.buttonText,
                text: values.text || '',
                url: values.url,
                order: siteBodyBlock?.bannerItems.length
                    ? siteBodyBlock.bannerItems.length + 1
                    : 1,
                status: values.status || 'ACTIVE',
                endDate: values.startDate || null,
                startDate: values.endDate || null,
                fileId: values.fileImage?.id || null,
                mobileFileId: values.mobileFileImage?.id || null,
                videoFileId: values.fileVideo?.id || null,
                mobileVideoFileId: values.mobileFileVideo?.id || null,
                linkType: values.linkType || 'SITE',
            };

            const fullData = siteBodyBlock?.bannerItems
                ? [...siteBodyBlock.bannerItems, requestData]
                : [requestData];

            dispatch(
                editBannerItemsWeb({ key: WebBlockKey.WEB_MAIN, newData: fullData }),
            );
        }
    };

    const validationSchema = yup.object().shape({
        title: yup
            .string()
            .max(50, 'Не более 50 символов')
            .required('Поле является обязательным'),
        text: yup.string().max(40, 'Не более 40 символов'),
        url: yup.string().required('Поле является обязательным'),
    });

    const form = useFormik<MainBannerItem>({
        onSubmit: handleSubmit,
        initialValues: { status: 'INACTIVE' } as MainBannerItem,
        validationSchema,
    });

    const handleToggle = (value: boolean) => {
        const newStatus = value ? 'ACTIVE' : 'INACTIVE';
        form.setFieldValue('status', newStatus);
    };

    const handleChangePage = () => {
        changePage(PageStatus.MAIN);
    };

    return (
        <S.RootWrapper>
            <S.Wrapper>
                <PageTitle>Body сайта</PageTitle>
                <BreadcrumbsMainBanners
                    breadcrumbs={createMainBannerBreadcrumbs('Добавить')}
                    onClick={handleChangePage}
                />
                <S.Root>
                    <S.MainWrapper>
                        <S.StyledInput
                            name="title"
                            onChange={form.handleChange}
                            value={form.values.title}
                            placeholder="Заголовок (не более 50 символов)*"
                            typeInput="text"
                            maxLength={255}
                            title="Заголовок (не более 50 символов)*"
                            isTouched={form.touched.title}
                            error={form.errors.title}
                            count={form.submitCount}
                        />
                        <S.StyledInput
                            name="text"
                            onChange={form.handleChange}
                            value={form.values.text}
                            placeholder="Описание (не более 40 символов)"
                            typeInput="text"
                            maxLength={255}
                            title="Описание (не более 40 символов)"
                            isTouched={form.touched.text}
                            error={form.errors.text}
                            count={form.submitCount}
                        />
                        <S.StyledInput
                            name="buttonText"
                            onChange={form.handleChange}
                            value={form.values.buttonText}
                            placeholder="Текст кнопки"
                            typeInput="text"
                            maxLength={255}
                            title="Текст кнопки"
                            isTouched={form.touched.buttonText}
                            error={form.errors.buttonText}
                        />
                        <S.StyledInput
                            name="url"
                            onChange={form.handleChange}
                            value={form.values.url}
                            placeholder="URL*"
                            typeInput="text"
                            title="URL*"
                            isTouched={form.touched.url}
                            error={form.errors.url}
                            count={form.submitCount}
                        />

                        <S.StyledInput
                            name="id"
                            onChange={form.handleChange}
                            value={form.values.id}
                            placeholder="ID"
                            typeInput="text"
                            maxLength={255}
                            title="ID"
                            isTouched={form.touched.id}
                            error={form.errors.id}
                            disabled
                        />

                        <S.NumberInputWrapper>
                            <S.StyledInput
                                name="order"
                                onChange={form.handleChange}
                                value={form.values.order}
                                placeholder="Сортировка"
                                typeInput="number"
                                title="Сортировка"
                                isTouched={form.touched.order}
                                error={form.errors.order}
                            />
                        </S.NumberInputWrapper>
                    </S.MainWrapper>
                    <S.SwitchWrapper>
                        <S.Subtitle>Статус</S.Subtitle>
                        <Switch
                            name="status"
                            value={form.values.status === 'ACTIVE'}
                            onChange={handleToggle}
                            type="checkbox"
                            title
                        />
                    </S.SwitchWrapper>
                    <S.MainWrapper>
                        <UploadPhotoWithMiniature
                            name="fileImage"
                            title="Изображение для desktop версии сайта"
                            onChange={form.setFieldValue}
                            value={form.values.fileImage}
                            format="9.5:4"
                            maxSize={4}
                            fieldError={fieldError.image}
                            resetErrors={resetErrors}
                        />

                        <UploadPhotoWithMiniature
                            name="mobileFileImage"
                            title="Изображение для мобильной версии сайта"
                            onChange={form.setFieldValue}
                            value={form.values.mobileFileImage}
                            format="2:1"
                            maxSize={3}
                            fieldError={fieldError.mobileImage}
                            resetErrors={resetErrors}
                        />

                        <UploadVideoWithMiniature
                            name="fileVideo"
                            title="Видео для desktop версии сайта"
                            onChange={form.setFieldValue}
                            value={form.values.fileVideo}
                            format="9.5:4"
                            maxSize={10}
                            fieldError={fieldError.video}
                            resetErrors={resetErrors}
                        />

                        <UploadVideoWithMiniature
                            name="mobileFileVideo"
                            title="Видео для мобильной версии сайта"
                            onChange={form.setFieldValue}
                            value={form.values.mobileFileVideo}
                            format="9.5:4"
                            maxSize={10}
                            fieldError={fieldError.mobileVideo}
                            resetErrors={resetErrors}
                        />
                    </S.MainWrapper>
                </S.Root>
            </S.Wrapper>
            <S.SubmitButtons>
                <SubmitButtons
                    reset={() => changePage(PageStatus.MAIN)}
                    submit={form.submitForm}
                />
            </S.SubmitButtons>
        </S.RootWrapper>
    );
};
